import {
  AmountUnitTypeEnum,
  IOfferingRes,
  IPriceResSchema,
  IPriceTierSettingsReq,
  IQuoteDiscountRespSchema,
  IQuoteItemReqSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IRateResBaseSchema,
  IRateResSchema,
  OfferingTypesEnum,
  OnQuoteOfferingChangeAction,
  PriceModelEnum,
  ProductTypeEnum,
  QuoteOfferingProps,
  QuoteOfferingRemovalScopeEnum,
  QuoteOfferingReqSchema,
  QuoteStatusEnum,
  QuoteTypeEnum,
  RateBillingFrequencyEnum,
  RateStatusEnum,
  SelectedProductsWithinOffering,
  SelectedProductsWithinOfferingForm,
  getQuoteOfferingReqSchema,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { orderObjectsBy, sortByProductPosition } from '@monetize/utils/core';
import { parseISO } from 'date-fns/parseISO';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdInfo, MdLock } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../../api/axios';
import { useGetDiscountsByOfferingId } from '../../../../../api/productCatalogService';
import { useGetById } from '../../../../../api/queryUtils';
import {
  MBox,
  MCircularProgress,
  MCloseButton,
  MCustomIconButton,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MIcon,
  MStack,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { ExpandCollapseButton } from '../../../../../components/Monetize/ExpandCollapseButton';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { MEditableTextarea } from '../../../../../components/Monetize/MEditableTextarea';
import { QuoteDrawer } from '../../../../../components/Quotes/QuotesDrawer/QuoteDrawer';
import {
  QUOTE_DATE_FIELD_WIDTH,
  QUOTE_RATE_FIELD_WIDTH,
  quoteOfferingInitial,
} from '../../../../../constants/quotes';
import {
  useExperienceConfiguration,
  useOfferingRate,
  useQuoteOffering,
} from '../../../../../hooks';
import { useFlags } from '../../../../../services/launchDarkly';
import { logger } from '../../../../../services/logger';
import Sentry from '../../../../../services/sentry';
import {
  CUSTOMER_AUTOMATION_Anaplan_Apps,
  CUSTOMER_AUTOMATION_Anaplan_CoreSaas,
  CUSTOMER_AUTOMATION_Coveo,
  CUSTOMER_AUTOMATION_CustomField_MirrorQuantity,
  CUSTOMER_AUTOMATION_CustomField_RollUpQuantity,
  CUSTOMER_AUTOMATION_Fexa,
  CUSTOMER_AUTOMATION_LangChain,
  CUSTOMER_AUTOMATION_LangChain_requireSecondUpdate,
  CUSTOMER_AUTOMATION_QuoteOffering_QuantityAutomation,
  CUSTOMER_AUTOMATION_QuoteOffering_SetInitialQuantity,
  CUSTOMER_AUTOMATION_QuoteOffering_getInitialQuantityMap,
  CUSTOMER_AUTOMATION_RedOak_QuantityAutomation,
  CUSTOMER_AUTOMATION_liveEo,
  CUSTOMER_AUTOMATION_liveEo_requireSecondUpdate,
  QuantityAutomationProductsById,
  TEMP_SALES_DEMO_TELEPORT_calculateQuantityOfDerivedProduct,
  getQuantityAutomationForQuoteItem,
  getQuantityAutomationProductsById,
  teleport_QuantityAutomation,
} from '../../../../../types';
import {
  getOfferingWithSortedRate,
  getQuoteOfferingRateInputProps,
  sortRate,
} from '../../../../../utils';
import { arrayToObject } from '../../../../../utils/misc';
import {
  calculateModifiedQuoteItemFields,
  getRelevantOfferingStartDate,
  isNonProratedAdvanceProduct,
  isOfferingRateChangeAllowed,
} from '../../../../../utils/quotes';
import { useQuoteContext } from '../../quoteContext';
import { getOfferingList, hasMixedSubscriptionTimings } from '../../quoteUtils';
import {
  BulkOfferingFlow,
  BulkOfferingItemQuantityHandlerProps,
} from '../BulkOfferingFlow/BulkOfferingFlow';
import { ImportQuoteOfferingsModal } from '../ImportQuoteOfferingsModal';
import { ManageProducts } from './manageProducts/ManageProducts';
import QuoteItems from './QuoteItems';
import { QuoteOfferingActions } from './QuoteOfferingActions';
import { QuoteOfferingActionsAmendV2 } from './QuoteOfferingActionsAmendV2';
import { QuoteOfferingAmount } from './QuoteOfferingAmount';
import QuoteOfferingDate from './QuoteOfferingDate';
import QuoteOfferingDiscount from './QuoteOfferingDiscount';
import {
  QuoteOfferingInactiveItem,
  QuoteOfferingInactiveRateItem,
  getIsRateEndDateIsBeforeContractStartDate,
} from './QuoteOfferingInactiveItem';
import QuoteOfferingRateRightEl from './QuoteOfferingRateRightEl';
import { QuoteOfferingScheduleChange } from './QuoteOfferingScheduleChange';
import { QuoteOfferingScheduledEffectiveDate } from './QuoteOfferingScheduledEffectiveDate';
import { QuoteOfferingScheduledEndDate } from './QuoteOfferingScheduledEndDate';
import {
  OfferingToggleButton,
  QuoteOfferingCollapsibleRate,
  RateToggleButton,
  clampValueToRange,
  getAreAllRatesExpanded,
  getCollapsedRateSummary,
  getQuoteOfferingState,
  getRateRightElement,
  getScheduleChangeState,
} from './quoteOfferingUtils';
import { RateItemContent } from './RateItemContent';
import { useDiscountDisabledProducts } from './useDiscountDisabledProducts';
import {
  useHandleAnaplanCustomFieldChange,
  useHandleCustomFieldMirrorAutomationEvent,
  useHandleFexaAutomationEvent,
  useHandleQuoteOfferingAutomationEvent,
} from './useHandleQuoteOfferingAutomationEvent';
import { useManageProduct } from './useManageProduct';
import { useQuoteOfferingAmendV2 } from './useQuoteOfferingAmendV2';

const EMPTY_AUTOMATION_PRODUCTS_BY_ID: QuantityAutomationProductsById = {};

const QuoteOffering = React.forwardRef<any, QuoteOfferingProps>(
  (props: QuoteOfferingProps, ref) => {
    let { quoteOffering } = props;

    const {
      parentQuoteOffering,
      priorScheduledQuoteOffering,
      nextScheduledQuoteOffering,
      index: offeringIndex,
      childQuoteOfferings = [],
      isChildOffering = false,
      isRootOfferingOpen = false,
      isOfferingOpen: isOfferingOpenProp,
      onIsOfferingOpenChange,
      isRateOpen: isRateOpenProp,
      onIsRateOpenChange,
      expandRatesSignal,
      quotePrices,
      onChange: onOfferingChange,
      quoteOfferings,
      setParentScheduleChangeActiveProp,
    } = props;

    const {
      teleportSalesDemoTempDependentPricingConfig,
      customerAutomationAcuityMd,
      customerAutomationTeleport,
      quoteOfferingDateAndScheduledChangeRate,
      customerAutomationLiveEo,
      customerAutomationLangChain,
      customerAutomationCoveo,
      customerAutomationFexa,
      customerAutomationRedOak,
      customerAutomationCustomFieldQuantity,
      customerAutomationAnaplan,
    } = useFlags();

    const {
      enableBulkAddQuoteOffering,
      allowChangingQuoteOfferingStartAndEndDate,
    } = useExperienceConfiguration(
      ExperienceConfigurationKeys.quoteAndContractSettings,
    );

    const hasChildOfferings = !!childQuoteOfferings.length;
    const hasNoScheduleChange =
      !isChildOffering && childQuoteOfferings?.length === 0;
    const {
      productOfferingsData,
      quoteData,
      quoteStateData,
      isLoading,
      isReadOnly: isReadOnlyQuote,
      useAmendmentV2,
      quoteContacts: { accountDetails },
      quoteSettingsData,
      methods: quoteFormMethods,
    } = useQuoteContext();
    const {
      quote,
      displayConfig,
      quoteDataOnInitialLoad,
      fetchQuote,
      updateQuoteCustomFields,
    } = quoteData;
    const { offeringLoadingState, setOfferingLoadingState } = quoteStateData;
    const account = accountDetails || {
      id: quote?.accountId || '',
      accountName: quote?.accountName || '',
    };

    const isOfferingLoading =
      offeringLoadingState[quoteOffering?.id || '']?.isLoading || false;
    /** Offering form becomes locked if a rate is being modified */
    const isRateLoading =
      offeringLoadingState[quoteOffering?.id || '']?.isLoading &&
      offeringLoadingState[quoteOffering?.id || '']?.modifiedItems
        ?.get?.('header')
        ?.has('rateId');
    const isOfferingDeleting =
      offeringLoadingState[quoteOffering?.id || '']?.isDeleting ||
      offeringLoadingState[parentQuoteOffering?.id || '']?.isDeleting ||
      false;

    const anyOfferingLoading = Object.values(offeringLoadingState).some(
      (item) => item.isLoading,
    );

    const {
      loading: isLoadingOfferings,
      productOfferings,
      addOrUpdateOfferingRate,
    } = productOfferingsData;

    const quoteOfferingState = getQuoteOfferingState({
      ...props,
      quote,
      quoteDataOnInitialLoad,
    });

    const {
      newlyAddedOffering,
      isNewQuoteOfferingForm,
      isLastSegment,
      isRemoved,
      isAmendment,
      isRenewal,
      isLocked,
      isReadOnlyAndNotEmptyOffering,
      allowPriceCustomization,
      gridColumns,
      isQuoteOfferingDiscountAvailable,
      bgColorV2,
      isOfferingUnselectable,
      isOfferingOnetime,
      isUsingDelayedBilling,
    } = quoteOfferingState;

    const [scheduleChangeActive, setScheduleChangeActive] = useState(false);
    const [parentScheduleChangeActive, setParentScheduleChangeActive] =
      useState(false); // Parent quote offering should know if child schedule change is being created
    const [scheduleChangeLoading, setScheduleChangeLoading] = useState(false);

    const [localIsRateOpen, setLocalIsRateOpen] = useState<boolean>(true);
    const isOpen = isRateOpenProp ?? localIsRateOpen;
    const setIsOpen = useCallback(
      (value: React.SetStateAction<boolean>) => {
        setLocalIsRateOpen((prev) => {
          const next = typeof value === 'function' ? value(prev) : value;
          onIsRateOpenChange?.(next);
          return next;
        });
      },
      [onIsRateOpenChange],
    );
    const [childRateOpenStates, setChildRateOpenStates] = useState<
      Partial<Record<string, boolean>>
    >({});
    const areAllRatesExpanded = getAreAllRatesExpanded({
      isParentRateOpen: isOpen,
      childQuoteOfferings,
      childRateOpenStates,
    });
    const handleRatesOpenChange = useCallback(
      (expanded: boolean) => {
        setIsOpen(expanded);
        setChildRateOpenStates(
          Object.fromEntries(
            childQuoteOfferings.map(({ id }) => [id, expanded] as const),
          ),
        );
      },
      [childQuoteOfferings, setIsOpen],
    );
    // when the table-level expand all fires, open the rate sections even if this offering was already open
    const prevExpandRatesSignalRef = useRef(expandRatesSignal);
    useEffect(() => {
      if (
        expandRatesSignal !== undefined &&
        expandRatesSignal !== prevExpandRatesSignalRef.current
      ) {
        handleRatesOpenChange(true);
      }
      prevExpandRatesSignalRef.current = expandRatesSignal;
    }, [expandRatesSignal, handleRatesOpenChange]);
    const [localIsOfferingOpen, setLocalIsOfferingOpen] =
      useState<boolean>(true);
    const isOfferingOpen = isOfferingOpenProp ?? localIsOfferingOpen;
    const setIsOfferingOpen = useCallback(
      (value: React.SetStateAction<boolean>) => {
        setLocalIsOfferingOpen((prev) => {
          const next = typeof value === 'function' ? value(prev) : value;
          onIsOfferingOpenChange?.(next);
          return next;
        });
      },
      [onIsOfferingOpenChange],
    );
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

    const [showUnlockTooltip, setShowUnlockTooltip] = useState<boolean>(false);

    const [isBulkOfferingLoading, setIsBulkOfferingLoading] = useState(false);
    const [isProductsLoading, setProductsLoading] = useState(false);

    const {
      createQuoteOffering,
      updateQuoteOffering,
      revertQuoteOffering,
      removeQuoteOffering,
      loading: isQuoteOfferingProcessing,
    } = useQuoteOffering(quote?.id!);

    const {
      offeringRate,
      orderedPricesUnderProduct,
      setOrderedPricesUnderProduct,
      setOfferingRate,
      fetchOfferingRate,
      loading: rateLoading,
    } = useOfferingRate(quoteOffering?.id, quoteOffering?.rateId);

    const quoteOfferingFormState = useForm<IQuoteOfferingReqSchema>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: getQuoteOfferingReqSchema(offeringRate),
      defaultValues: quoteOffering || {
        ...quoteOfferingInitial,
        options: {},
        endDate: quote?.contractEndDate,
      },
    });

    const {
      reset,
      watch,
      control,
      setValue,
      setError,
      handleSubmit,
      getValues,
      trigger,
      formState: { errors, isDirty, isValid, dirtyFields },
    } = quoteOfferingFormState;

    /**
     * We may need to update the quantity based on other quote offering quantity updates.
     * Listen for changes from other quote offerings
     */
    useHandleQuoteOfferingAutomationEvent(
      quoteOffering,
      quoteOfferingFormState,
      () => handleSubmit((data) => onSubmit({ data }), onError)(),
    );

    // The CMMS source offering re-fetches the quote once this save finishes
    useHandleFexaAutomationEvent(
      customerAutomationFexa,
      quoteOffering,
      quoteOfferingFormState,
      () =>
        handleSubmit(
          (data) => onSubmit({ data, skipParentUpdate: true }),
          onError,
        )(),
    );

    useEffect(() => {
      if (quote && isNewQuoteOfferingForm) {
        setValue('endDate', quote?.contractEndDate);
      }
    }, [quote, isNewQuoteOfferingForm, setValue]);

    const {
      isManageProductModalOpen,
      onManageProductModalOpen,
      onManageProductModalClose,
      handleOnModalClose,
      isDrawerOpen,
      onDrawerOpen,
      onDrawerClose,
    } = useManageProduct({ setValue });

    const watchOffering = watch('offeringId');
    const watchItems = watch('items');
    const watchDiscountIds = watch('discountIds');
    const watchRateId = watch('rateId');
    const watchDescription = watch('description');

    // Always ensure items are sorted by position to prevent re-ordering
    const sortedWatchItems = useMemo(
      () => (watchItems ? sortByProductPosition(watchItems) : []),
      [watchItems],
    );
    const contractStartDate =
      quote?.type === QuoteTypeEnum.AMENDMENT
        ? quote?.contractAmendmentDate
        : quote?.contractStartDate;

    // /api/offerings/{offeringId}
    // Checks for inactive offering
    const isSelectedOfferingActive = useMemo(() => {
      // While the catalog is loading we optimistically treat the offering as
      // active. An empty-but-loaded catalog means it is not active and has to
      // be fetched by id instead.
      if (!watchOffering || isLoadingOfferings) {
        return true;
      }
      return productOfferings.some(({ id }) => id === watchOffering);
    }, [watchOffering, productOfferings, isLoadingOfferings]);

    const showDuplicatedOfferingWarn = useMemo(() => {
      // if offering is not selected, return false
      if (
        !watchOffering ||
        !quoteOfferings?.length ||
        isRateLoading ||
        isOfferingLoading ||
        isLoadingOfferings
      ) {
        return false;
      }
      return !!quoteOfferings.find((offering, i) =>
        offeringIndex >= 0
          ? offeringIndex !== i &&
            offering.quoteOffering.offeringId === watchOffering
          : offering.quoteOffering.offeringId === watchOffering,
      );
    }, [watchOffering, quoteOfferings]);

    // Fetch inactive offering
    const { data: inactiveOffering, refetch: refetchInactiveOffering } =
      useGetById<IOfferingRes>('productCatalogOfferings', watchOffering!, {
        enabled:
          !isNewQuoteOfferingForm &&
          !!watchOffering &&
          !isSelectedOfferingActive &&
          !drawerOpen, // should not try to fetch inactive offering when prices drawer is open
        select: (data) =>
          getOfferingWithSortedRate(data, {
            accountId: quote?.accountId,
            currency: quote?.currency,
            contractStartDate,
            rateId: watchRateId,
          }),
      });

    /**
     * The selected offering with its quotable rates. Derived from the form's
     * offering id rather than synced through effects, so the row is complete on
     * the first render that has the catalog instead of filling in over several
     * passes. An offering no longer in the catalog comes from the by-id fetch.
     */
    const offering = useMemo<IOfferingRes | undefined>(() => {
      if (!watchOffering) {
        return undefined;
      }
      const catalogOffering = productOfferings.find(
        ({ id }) => id === watchOffering,
      );
      if (catalogOffering) {
        return getOfferingWithSortedRate(catalogOffering, {
          accountId: quote?.accountId,
          currency: quote?.currency,
          contractStartDate,
          rateId: watchRateId,
        });
      }
      return inactiveOffering?.id === watchOffering
        ? inactiveOffering
        : undefined;
    }, [
      watchOffering,
      productOfferings,
      inactiveOffering,
      quote?.accountId,
      quote?.currency,
      contractStartDate,
      watchRateId,
    ]);

    /**
     * Catalog products (with their custom fields) for every offering the quote
     * can reference, keyed by product id. Drives the custom-field quantity
     * automation, which needs product flags for lines in other offerings too.
     *
     * Declared after `offering`, which it reads; `offering` is derived rather
     * than held in state, so it is no longer available at the top of the body.
     */
    const automationProductsById = useMemo(() => {
      if (!customerAutomationCustomFieldQuantity) {
        return EMPTY_AUTOMATION_PRODUCTS_BY_ID;
      }
      return getQuantityAutomationProductsById(
        offering ? [...productOfferings, offering] : productOfferings,
      );
    }, [customerAutomationCustomFieldQuantity, productOfferings, offering]);

    useHandleCustomFieldMirrorAutomationEvent(
      customerAutomationCustomFieldQuantity,
      automationProductsById,
      quoteOffering,
      quoteOfferingFormState,
      () => handleSubmit((data) => onSubmit({ data }), onError)(),
    );

    const scheduleChangeState = getScheduleChangeState({
      isChildOffering,
      quoteOffering,
      quote,
      offering,
    });
    const { monthInterval } = scheduleChangeState;

    const { discountDisabledProductIds } = useDiscountDisabledProducts(
      offering?.offeringProducts || [],
    );

    useEffect(() => {
      if (inactiveOffering) {
        if (quoteOffering?.items) {
          setValue(
            'items',
            sortByProductPosition(
              quoteOffering.items.map((item) => ({
                ...item,
                incrementalQuantity: item.incrementalQuantity,
                customPrice: item.customPriceId
                  ? {
                      id: item.customPriceId,
                      unitAmount: item.customPrice?.amount || 0,
                    }
                  : undefined,
              })),
            ),
          );
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inactiveOffering]);

    // /api/rates/{rateId}
    // Check for inactive rate
    const isSelectedRateActive = useMemo(() => {
      if (!watchRateId || !offering?.rates?.length) {
        return true;
      }
      return Boolean(
        watchRateId &&
        offering &&
        offering?.rates.find(({ id }) => id === watchRateId),
      );
    }, [watchRateId, offering]);

    // Fetch inactive rate
    const { data: inactiveRate } = useGetById<IRateResSchema>(
      'productCatalogRates',
      watchRateId!,
      {
        enabled:
          !isNewQuoteOfferingForm &&
          !!watchRateId &&
          !isSelectedRateActive &&
          !drawerOpen, // should not try to fetch inactive rate when prices drawer is open
      },
    );

    useEffect(() => {
      if (inactiveRate) {
        // Parse contract start date
        const parsedContractStartDate = quote
          ? parseISO(quote.contractStartDate)
          : undefined;
        const isRateEndDateIsBeforeContractStartDate =
          getIsRateEndDateIsBeforeContractStartDate(
            inactiveRate,
            parsedContractStartDate,
          );
        if (
          offering &&
          inactiveRate.status === RateStatusEnum.ACTIVE &&
          inactiveRate.quotable &&
          !isRateEndDateIsBeforeContractStartDate
        ) {
          // By any chance active rate is not synced with the product
          // offerings it should manually update
          addOrUpdateOfferingRate(offering.id, inactiveRate);
        }
      }
      // TODO: validate this works without the other deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inactiveRate]);

    const offeringRatesById = useMemo(() => {
      const ratesById: Record<string, IRateResBaseSchema> = Array.isArray(
        offering?.rates,
      )
        ? arrayToObject(offering.rates, 'id')
        : {};
      if (inactiveRate) {
        ratesById[inactiveRate.id] = inactiveRate;
      }
      return ratesById;
    }, [offering?.rates, inactiveRate]);

    /**
     * Display name for a rate id. The map above is the single source: it already
     * merges the separately fetched inactive rate in by id. The quote response
     * names its own rate, so fall back to that rather than render a blank row
     * while the catalog loads or when the rate has since been deleted.
     */
    const getRateName = (rateId?: string | null) => {
      if (!rateId) {
        return '';
      }
      if (offeringRatesById[rateId]?.name) {
        return offeringRatesById[rateId].name;
      }
      return rateId === quoteOffering?.rateId
        ? (quoteOffering?.rateName ?? '')
        : '';
    };

    const rateSelects: [IRateResBaseSchema[], IRateResBaseSchema[]] =
      useMemo(() => {
        let list: IRateResBaseSchema[] = [];
        let disabledList: IRateResBaseSchema[] = [];
        const allOfferings = inactiveOffering
          ? [...productOfferings, inactiveOffering]
          : productOfferings;
        const selectedOffering = allOfferings.find(
          ({ id }) => id === watchOffering,
        );

        if (selectedOffering) {
          if (selectedOffering?.rates) {
            if (inactiveRate) {
              const isInactiveRateExistInOffering = selectedOffering.rates.find(
                ({ id }) => id === inactiveRate.id,
              );
              if (isInactiveRateExistInOffering) {
                list = selectedOffering.rates;
              } else {
                list = [...selectedOffering.rates, inactiveRate];
              }
            } else {
              list = selectedOffering.rates;
            }
          }
        }

        const parentRate = quoteOffering?.rateId
          ? offeringRatesById[quoteOffering.rateId]
          : null;
        // Making sure if there is a parentRate
        if (parentRate) {
          disabledList = list.filter(
            ({ billingFrequencyInMonths, subscriptionTiming }) =>
              !(
                billingFrequencyInMonths ===
                  parentRate.billingFrequencyInMonths &&
                subscriptionTiming === parentRate.subscriptionTiming
              ),
          );
        }

        if (
          inactiveRate &&
          quoteOffering?.rateId &&
          quoteOffering.rateId === inactiveRate?.id
        ) {
          disabledList = [inactiveRate];
        }

        return [sortRate(list), disabledList];
      }, [
        quoteOffering,
        inactiveOffering,
        productOfferings,
        offeringRatesById,
        inactiveRate,
        watchOffering,
      ]);

    const isMixedSubscriptionTiming = hasMixedSubscriptionTimings(
      rateSelects[0],
    );

    const { data: discounts } = useGetDiscountsByOfferingId({
      offeringId: watchOffering!,
      filters: quote?.accountId
        ? {
            accountId: quote?.accountId ?? '',
            rateId: quoteOffering?.rateId ?? '',
          }
        : {},
      options: { enabled: !!watchOffering, refetchOnWindowFocus: false },
    });

    const mergedDiscounts = useMemo(() => {
      if (!discounts) {
        return null;
      }
      const activeIds = new Set(discounts.map(({ id }) => id));
      const appliedInactive = (quoteOffering?.discounts ?? []).filter(
        ({ id }) => !activeIds.has(id),
      );
      if (!appliedInactive.length) {
        return discounts;
      }
      return orderObjectsBy([...discounts, ...appliedInactive], ['name']);
    }, [discounts, quoteOffering?.discounts]);

    const displayImportOffering =
      !watchOffering && isNewQuoteOfferingForm && !quote?.quoteOfferings.length;
    // load prices for this offering and rate
    useEffect(() => {
      if (watchRateId && !offeringRate && !rateLoading) {
        fetchOfferingRate(watchRateId);
      }
    }, [watchRateId, offeringRate]);

    /**
     * When quoteOffering changes, reset form state.
     * Quantity and Discount are only retained from the form when the user has
     * an actual unsaved edit in that field (RHF dirtyFields) -- otherwise the
     * fresh server value wins. This lets server-driven updates (e.g. the AI
     * chat assistant editing a quote) show up without clobbering an in-progress
     * edit the user is actively typing.
     * There are other places in component that may correct quantity and discount if they are invalid
     */
    useEffect(() => {
      if (quoteOffering) {
        const {
          description,
          offeringId,
          rateId,
          items,
          discounts: offeringDiscounts,
          startDate,
          endDate,
          options,
        } = quoteOffering;
        let discountIds: string[] = [];
        const sortedQuoteItems = sortByProductPosition(items);

        const hasOfferingDiscounts = offeringDiscounts?.length > 0;
        if (hasOfferingDiscounts) {
          discountIds = offeringDiscounts.map(
            (discount: IQuoteDiscountRespSchema) => discount.id,
          );
        }

        const formValues = getValues();

        const formItemsById = arrayToObject(formValues.items, 'id');
        type ItemDirtyFields = NonNullable<typeof dirtyFields.items>[number];
        const dirtyFieldsByItemId: Record<string, ItemDirtyFields | undefined> =
          {};
        formValues.items?.forEach((formValueItem, index) => {
          if (formValueItem?.id) {
            dirtyFieldsByItemId[formValueItem.id] = dirtyFields.items?.[index];
          }
        });

        // Fields with an unsaved user edit (RHF dirtyFields) are re-applied
        // after reset() below, so reset() itself always uses the fresh
        // server values here.
        const dirtyFieldReapplies: {
          path: `items.${number}.${
            | 'quantity'
            | 'customDiscountAmountOrPercent'
            | 'customDiscountType'
            | 'customDiscountLifecyclePolicies'}`;
          value: unknown;
        }[] = [];

        // Create the items array, ensuring we preserve the sorted order by position
        const resetItems = sortedQuoteItems.map((item, index) => {
          const formItem = formItemsById[item.id];
          const itemDirtyFields = dirtyFieldsByItemId[item.id];

          // Only keep the form quantity if the user has an unsaved edit in it,
          // otherwise take the fresh server quantity
          if (isNil(item.incrementalQuantity) && itemDirtyFields?.quantity) {
            dirtyFieldReapplies.push({
              path: `items.${index}.quantity`,
              value: formItem?.quantity ?? item.quantity,
            });
          }
          // Only keep form discount values if the user has an unsaved edit in them
          if (itemDirtyFields?.customDiscountAmountOrPercent) {
            dirtyFieldReapplies.push({
              path: `items.${index}.customDiscountAmountOrPercent`,
              value:
                formItem?.customDiscountAmountOrPercent ??
                item.customDiscountAmountOrPercent,
            });
          }
          if (itemDirtyFields?.customDiscountType) {
            dirtyFieldReapplies.push({
              path: `items.${index}.customDiscountType`,
              value: formItem?.customDiscountType ?? item.customDiscountType,
            });
          }
          if (itemDirtyFields?.customDiscountLifecyclePolicies) {
            dirtyFieldReapplies.push({
              path: `items.${index}.customDiscountLifecyclePolicies`,
              value:
                formItem?.customDiscountLifecyclePolicies ??
                item.customDiscountLifecyclePolicies ??
                [],
            });
          }

          return {
            ...item,
            customPrice: item.customPriceId
              ? {
                  id: item.customPriceId,
                  unitAmount: item.customPrice?.amount || 0,
                }
              : undefined,
            quantity: item.quantity,
            customDiscountAmountOrPercent: item.customDiscountAmountOrPercent,
            customDiscountType: item.customDiscountType,
            ...(quote?.type === QuoteTypeEnum.RENEWAL && {
              incrementalQuantity: item.incrementalQuantity ?? item.quantity,
            }),
            // Explicitly preserve position to ensure sort order is maintained
            position: item.position,
            customDiscountLifecyclePolicies:
              item.customDiscountLifecyclePolicies ?? [],
          };
        });

        reset({
          description,
          offeringId,
          rateId,
          discountIds,
          startDate,
          endDate,
          options,
          items: resetItems,
          schedule: quoteOffering.parentQuoteOfferingId
            ? {
                startDate: quoteOffering.startDate,
                endDate: quoteOffering.endDate,
                parentQuoteOfferingId: quoteOffering.parentQuoteOfferingId,
              }
            : undefined,
        });

        // reset() above treats resetItems as the new defaults, which would
        // otherwise clear dirtyFields/isDirty for these fields even though
        // their in-progress value was preserved -- re-mark them dirty so the
        // pending edit still gets submitted on blur (isDirty is checked in
        // handleQuoteOffering).
        dirtyFieldReapplies.forEach(({ path, value }) => {
          setValue(path, value as never, { shouldDirty: true });
        });

        // reset() also clears formState.errors, so a restored value that was
        // actually invalid (e.g. an out-of-range discount the user was still
        // typing) would otherwise show as error-free until the next
        // validation event -- revalidate exactly the restored fields.
        if (dirtyFieldReapplies.length > 0) {
          trigger(dirtyFieldReapplies.map(({ path }) => path));
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quoteOffering]);

    const areAllItemsInOfferingMandatory = (selectedOffering: IOfferingRes) => {
      const allItems = selectedOffering?.offeringProducts || [];
      return (
        allItems.length &&
        allItems.every((item: { isMandatory: boolean }) => item.isMandatory)
      );
    };

    const getAutoSelectRate = () => {
      const catalogRates = rateSelects[0].filter(
        (currenRate) => !currenRate.parentRateId,
      );

      if (catalogRates.length === 1) {
        return catalogRates[0];
      }

      // Select the first annual rate if exists or select the first rate available
      const firstAnnualRate = catalogRates.find(
        (rate) => rate.billingFrequency === RateBillingFrequencyEnum.ANNUALLY,
      );
      return firstAnnualRate || catalogRates[0];
    };

    const handleOfferingChange = async (offeringId: string) => {
      const selectedOffering = productOfferings.find(
        ({ id }) => id === offeringId,
      );

      // On offering change we should set rateId as null
      setValue('rateId', null);

      // `offering` itself follows the form's offering id, which the caller has
      // already set; only the initial items need building here.
      if (selectedOffering) {
        const quoteItems = selectedOffering.offeringProducts?.map(
          ({ product, position }) => {
            const defaultDisplayUnitPriceFrequency =
              product.displayUnitPriceFrequency ??
              quoteSettingsData.quoteSettings?.defaultDisplayUnitPriceFrequency;
            const options = defaultDisplayUnitPriceFrequency
              ? {
                  showPrices: false,
                  displayUnitPriceFrequency: defaultDisplayUnitPriceFrequency,
                }
              : undefined;

            if (isNonProratedAdvanceProduct(product)) {
              return {
                productId: product.id,
                quantity: 0,
                incrementalQuantity: 0,
                position,
                customDiscountAmountOrPercent: null,
                prorationPolicy: product.prorationPolicy,
                options,
              } as IQuoteItemReqSchema;
            }

            return {
              productId: product.id,
              quantity: 0,
              position,
              customDiscountAmountOrPercent: null,
              options,
            } as IQuoteItemReqSchema;
          },
        );

        if (quoteItems) {
          setValue('items', quoteItems);
        }

        if (!areAllItemsInOfferingMandatory(selectedOffering)) {
          onManageProductModalOpen(); // open manage product modal
        }
      }
      handleQuoteOffering();
    };

    const handleScheduleChangeInitiated = () => {
      setScheduleChangeActive(true);
    };

    const handleAddScheduledChange = async ({
      startDate,
      endDate,
      rateId,
      quoteOfferingIdToCopy,
    }: {
      startDate: string;
      endDate?: string;
      rateId: string;
      // If provided, use the quote offering with the given id to copy the schedule from
      quoteOfferingIdToCopy?: string;
    }) => {
      const allOfferings = [
        quoteOffering,
        ...(childQuoteOfferings || []),
      ].filter((offering) => !!offering) as IQuoteOfferingRespSchema[];

      const lastQuoteOffering = quoteOfferingIdToCopy
        ? allOfferings.find((offering) => offering.id === quoteOfferingIdToCopy)
        : allOfferings.length > 0
          ? allOfferings[allOfferings.length - 1]
          : quoteOffering;
      // Create offering, show loading, then remove the temp scheduled offering
      // set scheduled change loading
      if (!lastQuoteOffering) {
        return null;
      }

      try {
        setScheduleChangeLoading(true);

        // Find the product's proration policy from offering
        const getIsProductProrationPolicy = (productId: string) => {
          const offeringProduct = offering?.offeringProducts?.find(
            ({ product }) => product.id === productId,
          );
          return isNonProratedAdvanceProduct(offeringProduct?.product);
        };

        const newOffering: IQuoteOfferingReqSchema = {
          offeringId: lastQuoteOffering.offeringId,
          rateId,
          subscriptionId: lastQuoteOffering.subscriptionId,
          description: lastQuoteOffering.description,
          items: lastQuoteOffering.items.map((item) => {
            const isProductProrationPolicy = getIsProductProrationPolicy(
              item.productId,
            );
            return {
              ...item,
              customPrice: item.customPriceId
                ? {
                    unitAmount: item.customPrice?.amount || 0,
                  }
                : undefined,
              ...(isProductProrationPolicy && {
                incrementalQuantity: 0,
              }),
            };
          }),
          discountIds: lastQuoteOffering.discounts.map(({ id }) => id),
          schedule: {
            parentQuoteOfferingId: lastQuoteOffering.parentQuoteOfferingId
              ? lastQuoteOffering.parentQuoteOfferingId
              : lastQuoteOffering.id,
            startDate,
            endDate,
          },
        };

        // The copied segment inherits the prior period's quantities. Re-derive
        // the custom-field automation against this period so quantity-locked
        // lines mirror the source segment that overlaps it, not the prior one.
        const mirrorOnSuccessCallback =
          CUSTOMER_AUTOMATION_CustomField_MirrorQuantity({
            customerAutomationCustomFieldQuantity,
            productsById: automationProductsById,
            quote,
            quoteItemsToModify: newOffering,
          });
        CUSTOMER_AUTOMATION_CustomField_RollUpQuantity({
          customerAutomationCustomFieldQuantity,
          productsById: automationProductsById,
          quoteItemsToModify: newOffering,
        });

        const parsedNewOffering = QuoteOfferingReqSchema.safeParse(newOffering);
        if (!parsedNewOffering.success) {
          Sentry.captureException(parsedNewOffering.error.issues, {
            tags: {
              type: 'QUOTE_OFFERING_FORM',
            },
          });
        }
        const newOfferingResponse = await createQuoteOffering(
          parsedNewOffering.success ? parsedNewOffering.data : newOffering,
        );
        setScheduleChangeActive(false);
        // returns null on failure
        if (!newOfferingResponse) {
          return null;
        }
        mirrorOnSuccessCallback?.(newOfferingResponse);
        return newOfferingResponse;
      } catch (ex) {
        handleApiErrorToast(ex);
      } finally {
        setScheduleChangeLoading(false);
      }
      return null;
    };

    const handleRemoveScheduledChange = async () => {
      try {
        if (quoteOffering) {
          setScheduleChangeLoading(true);
          await removeQuoteOffering({
            removal_scope: QuoteOfferingRemovalScopeEnum.TARGET,
            target_id: quoteOffering?.id,
          });
          await onOfferingChange('DELETE', quoteOffering);
        }
      } catch (ex) {
        onError(ex);
      } finally {
        setScheduleChangeLoading(false);
      }
    };

    /**
     * Submit quote offering
     * All values sent to API are checked to see if they are actually modified
     * If nothing is modified, the API call is skipped
     *
     * The form dirty state is not reliable in this case because of a race condition for resetting the form while user is entering data
     * And string to number conversion
     *
     * @param overrideOfferingDirtyFields Offering fields that should be marked as dirty even if not modified (e.x. related rate fields)
     */
    const onSubmit = async ({
      data,
      overrideOfferingDirtyFields,
      isSecondUpdate = false,
      quoteOfferingOverride,
      doUpdateParent = true,
      skipParentUpdate = false,
    }: {
      data: IQuoteOfferingReqSchema;
      overrideOfferingDirtyFields?: string[];
      isSecondUpdate?: boolean;
      // Hack to allow this to be called twice (recursively) without relying on parent state which is stale before quote is updated
      quoteOfferingOverride?: Maybe<IQuoteOfferingRespSchema>;
      doUpdateParent?: boolean;
      /**
       * Don't tell the parent at all, not even in memory: the caller re-fetches
       * the quote once this save finishes. The in-memory update would be built
       * from a quote snapshot taken before the caller's own save, briefly
       * reverting the caller's offering on screen.
       */
      skipParentUpdate?: boolean;
    }) => {
      quoteOffering = quoteOfferingOverride || quoteOffering;

      if (!data.rateId || !quote) {
        return;
      }

      TEMP_SALES_DEMO_TELEPORT_calculateQuantityOfDerivedProduct({
        teleportSalesDemoTempDependentPricingConfig,
        quoteItemsToModify: data,
        setValue,
      });

      teleport_QuantityAutomation({
        customerAutomationTeleport,
        quoteItemsToModify: data,
        setValue,
      });

      CUSTOMER_AUTOMATION_liveEo({
        quoteOffering,
        quoteItemsToModify: data,
        customerAutomationLiveEo,
        setValue,
      });

      CUSTOMER_AUTOMATION_LangChain({
        quoteItemsToModify: data,
        customerAutomationLangChain,
        setValue,
      });

      CUSTOMER_AUTOMATION_Coveo({
        quoteOffering,
        quoteItemsToModify: data,
        customerAutomationCoveo,
        setValue,
      });

      // Runs for both add and update so targets are derived regardless of
      // whether the CMMS source shares this offering or lives in another one
      const fexaOnSuccessCallback = CUSTOMER_AUTOMATION_Fexa({
        customerAutomationFexa,
        quoteOffering,
        quote,
        quoteItemsToModify: data,
        setValue,
        // An event-triggered save (the only caller of skipParentUpdate) must
        // not broadcast back to the offering that is waiting on it
        suppressBroadcast: skipParentUpdate,
      });

      // Optional callback set from quantity automation if the update is successful
      let onSuccessCallback: (() => void) | undefined;

      if (quoteOffering) {
        onSuccessCallback =
          CUSTOMER_AUTOMATION_QuoteOffering_QuantityAutomation({
            customerAutomationAcuityMd,
            quoteOffering,
            quoteItemsToModify: data,
            setValue,
          });
      }

      CUSTOMER_AUTOMATION_RedOak_QuantityAutomation({
        customerAutomationRedOak,
        quoteItemsToModify: data,
        setValue,
      });

      // Mirror before roll-up so a roll-up sum sees the mirrored quantities.
      // Runs on add and update; the callback (given the saved offering) tells
      // other offerings holding quantity-locked lines to follow.
      const mirrorOnSuccessCallback =
        CUSTOMER_AUTOMATION_CustomField_MirrorQuantity({
          customerAutomationCustomFieldQuantity,
          productsById: automationProductsById,
          quoteOffering,
          quote,
          quoteItemsToModify: data,
          setValue,
        });

      CUSTOMER_AUTOMATION_CustomField_RollUpQuantity({
        customerAutomationCustomFieldQuantity,
        productsById: automationProductsById,
        quoteItemsToModify: data,
        setValue,
      });

      // The quote header form is updated synchronously on custom-field edits
      // and after an Apps save, unlike `quote`, a render snapshot. That matters
      // in a bulk add, which reuses this callback for every offering it creates.
      const latestQuoteCustomFields =
        quoteFormMethods.getValues('customFields') ?? quote.customFields;

      // Apps line: module discount now; the returned quote custom field patch
      // is persisted after the offering save succeeds (see below)
      const anaplanQuoteCustomFieldsUpdate = CUSTOMER_AUTOMATION_Anaplan_Apps({
        customerAutomationAnaplan,
        quoteCustomFields: latestQuoteCustomFields,
        quoteItemsToModify: data,
        setValue,
      });

      CUSTOMER_AUTOMATION_Anaplan_CoreSaas({
        customerAutomationAnaplan,
        quoteCustomFields: latestQuoteCustomFields,
        quoteItemsToModify: data,
        setValue,
      });

      // Destructured after the automations, which may rewrite `data` (e.g. the
      // discount selection) in place
      const {
        offeringId,
        rateId,
        items,
        discountIds,
        startDate,
        endDate,
        ...rest
      } = data;

      if (quoteOffering?.id) {
        const modifiedItems = calculateModifiedQuoteItemFields(
          data,
          quoteOffering,
          overrideOfferingDirtyFields,
        );
        // Ensure data is truly dirty before making API call
        if (
          Array.from(modifiedItems.values()).every(
            (itemDirtyFields) => itemDirtyFields.size === 0,
          )
        ) {
          logger.info('No changes detected, skipping quote offering update');
          return;
        }

        setOfferingLoadingState((prevValue) => {
          if (!quoteOffering) {
            return prevValue;
          }
          return {
            ...prevValue,
            [quoteOffering.id]: {
              id: quoteOffering.id,
              isLoading: true,
              isDeleting: false,
              modifiedItems,
            },
          };
        });
      }

      const rateDescription = offeringRatesById[rateId]?.description;

      // if the rate changes, default the description to the new rate's description - otherwise retain the existing description
      const currDescription =
        rateId !== quoteOffering?.rateId ? rateDescription : data.description;
      const description =
        currDescription || rateDescription || offering?.description;

      if (description !== data.description) {
        setValue('description', description);
      }

      const currentOffering = productOfferings.find(
        (prod) => prod.id === offeringId,
      );
      const productsById = arrayToObject(
        currentOffering?.products || offering?.products || [],
        'id',
      );

      // Fetch the current rate to get the latest price information
      const currentOfferingRate = await fetchOfferingRate(rateId);

      const quoteOfferingReq = {
        offeringId,
        rateId,
        discountIds: discountIds && discountIds.length > 0 ? discountIds : null, // if there is no discountIds it will always be customDiscount
        ...rest,
        description,
        items:
          items &&
          items.length > 0 &&
          items.map((quoteItem: IQuoteItemReqSchema) => {
            const product = productsById[quoteItem.productId];
            const isCustomPrice =
              currentOfferingRate?.prices.find(
                (price: IPriceResSchema) =>
                  price?.product?.id === quoteItem.productId,
              )?.priceModel === PriceModelEnum.CUSTOM;

            const discountFields: Pick<
              Partial<IQuoteItemReqSchema>,
              | 'customDiscountType'
              | 'customDiscountAmountOrPercent'
              | 'customDiscountDescription'
              | 'customDiscountName'
            > = {};

            if (
              product?.productType === ProductTypeEnum.ONETIME_PREPAID_CREDIT
            ) {
              discountFields.customDiscountType = null;
              discountFields.customDiscountAmountOrPercent = null;
              discountFields.customDiscountDescription = null;
              discountFields.customDiscountName = null;
            }

            const isNonProrated = isNonProratedAdvanceProduct(product);

            // Exclude incrementalQuantity from the spread if the product is not non-prorated
            const { incrementalQuantity, ...quoteItemWithoutIncrementalQty } =
              quoteItem;

            return {
              ...(isNonProrated ? quoteItem : quoteItemWithoutIncrementalQty),
              ...discountFields,
              description: quoteItem.description || product?.description,
              sku: quoteItem.sku || product?.sku,
              customPrice: isCustomPrice
                ? {
                    ...quoteItem.customPrice,
                    unitAmount: quoteItem.customPrice?.unitAmount || 0,
                  }
                : undefined,
              // Give default custom discount type when amount is greater than 0. It's not selected for optional product. https://monetizenow.atlassian.net/browse/BP-12377
              customDiscountType:
                quoteItem.customDiscountAmountOrPercent &&
                Number(quoteItem.customDiscountAmountOrPercent) > 0
                  ? AmountUnitTypeEnum.PERCENTAGE
                  : null,
              customDiscountLifecyclePolicies:
                quoteItem.customDiscountAmountOrPercent &&
                Number(quoteItem.customDiscountAmountOrPercent) > 0
                  ? (quoteItem.customDiscountLifecyclePolicies ?? [])
                  : [],
            };
          }),
      } as IQuoteOfferingReqSchema;

      const shouldSendOfferingDates =
        useAmendmentV2 || allowChangingQuoteOfferingStartAndEndDate;
      if (shouldSendOfferingDates) {
        quoteOfferingReq.startDate = startDate;
        quoteOfferingReq.endDate = endDate;
      }

      if (
        quoteOffering &&
        isChildOffering &&
        data.schedule?.startDate &&
        quoteOffering.startDate !== data.schedule?.startDate &&
        quoteOffering?.parentQuoteOfferingId
      ) {
        quoteOfferingReq.startDate = undefined;
        quoteOfferingReq.schedule = {
          startDate: data.schedule.startDate,
          parentQuoteOfferingId: quoteOffering.parentQuoteOfferingId,
        };
      } else if (quoteOffering && isChildOffering) {
        quoteOfferingReq.startDate = undefined;
      }

      if (!isChildOffering && quote) {
        quoteOfferingReq.startDate = getRelevantOfferingStartDate(
          isAmendment,
          quote,
          quoteOfferingReq,
          offering,
        );
      }

      // Bulk add submits through the placeholder row, which has no `offering`
      const isSubmittedOfferingOneTime =
        isCurrentOfferingOneTime ||
        currentOffering?.type === OfferingTypesEnum.ONETIME;
      // One-time offerings end on their start date
      if (shouldSendOfferingDates && isSubmittedOfferingOneTime) {
        quoteOfferingReq.endDate = quoteOfferingReq.startDate;
      }

      let offeringRes: IQuoteOfferingRespSchema | null = null;
      let action: OnQuoteOfferingChangeAction;
      // Check if previous offering id and current offering id is equal
      if (quoteOffering && quoteOffering.id) {
        action = 'UPDATE';
        if (offeringId === quoteOffering.offeringId) {
          offeringRes = await updateQuoteOffering(
            quoteOffering.id,
            quoteOfferingReq,
          );
          if (isNull(offeringRes)) {
            const transformedOffering = {
              ...quoteOffering,
              items: quoteOffering.items?.map((item) => ({
                ...item,
                customPrice: item.customPrice
                  ? {
                      id: item.customPrice.id,
                      unitAmount: item.customPrice.amount || 0,
                    }
                  : undefined,
              })),
            };

            reset({
              ...transformedOffering,
              ...(quoteOfferingReq.schedule && {
                schedule: quoteOfferingReq.schedule,
              }),
            });
          }
        }
      } else {
        action = 'ADD';
        // Update initial quantity based on other quantities if quantity automation is configured - this checks all offerings on the quote
        CUSTOMER_AUTOMATION_QuoteOffering_SetInitialQuantity({
          customerAutomationAcuityMd,
          newQuoteOffering: quoteOfferingReq,
          quote,
          setValue,
        });
        offeringRes = await createQuoteOffering(quoteOfferingReq);
      }

      onSuccessCallback?.();
      if (offeringRes) {
        // Waits for the offerings holding the onboarding targets to save, so
        // the single quote re-fetch below shows their new price
        await fexaOnSuccessCallback?.(offeringRes);
        mirrorOnSuccessCallback?.(offeringRes);
      }

      // Awaited before `onOfferingChange` so its quote re-fetch cannot return
      // the previous custom fields. The patch is merged into the quote header
      // form's values, which already hold any custom-field edit made while this
      // offering was saving (`quote` is a snapshot from before the save), and
      // the result is written back to that form so a later full quote PUT does
      // not send a stale value.
      let didUpdateQuoteCustomFields = false;
      if (offeringRes && anaplanQuoteCustomFieldsUpdate) {
        const nextCustomFields = {
          ...(quoteFormMethods.getValues('customFields') ?? quote.customFields),
          ...anaplanQuoteCustomFieldsUpdate.customFieldsPatch,
        };
        // Null when the request failed (an error toast is shown); the form then
        // keeps the server value rather than mirroring an unsaved one
        const persistedCustomFields = await updateQuoteCustomFields(
          quote.id,
          nextCustomFields,
        );
        if (persistedCustomFields) {
          // Merge again now: a header field edited while the request was in
          // flight must keep its new value, only the automation's field is forced
          quoteFormMethods.setValue('customFields', {
            ...quoteFormMethods.getValues('customFields'),
            ...anaplanQuoteCustomFieldsUpdate.customFieldsPatch,
          });
          didUpdateQuoteCustomFields = true;
        }
      }

      const requiresSecondUpdate =
        !isSecondUpdate &&
        offeringRes &&
        (CUSTOMER_AUTOMATION_liveEo_requireSecondUpdate({
          customerAutomationLiveEo,
          priorQuoteOffering: quoteOffering,
          quoteOfferingResponse: offeringRes,
        }) ||
          CUSTOMER_AUTOMATION_LangChain_requireSecondUpdate({
            customerAutomationLangChain,
            priorQuoteOffering: quoteOffering,
            quoteOfferingResponse: offeringRes,
          }));

      // For newly added offering, reset the form state of the placeholder to add a new offering
      if (isNewQuoteOfferingForm) {
        reset({
          ...quoteOfferingInitial,
          endDate: quote?.contractEndDate,
        });
      }

      setOfferingLoadingState((prevValue) => {
        if (!quoteOffering) {
          return prevValue;
        }
        return {
          ...prevValue,
          [quoteOffering.id]: {
            ...prevValue[quoteOffering.id],
            isLoading: false,
            isDeleting: false,
          },
        };
      });

      // Quote rules may read the custom field the Apps save just changed
      const runEvaluate =
        didUpdateQuoteCustomFields && quote.status === QuoteStatusEnum.DRAFT;

      // Tell parent that data changes, this will re-fetch quote and update UI with price updates
      if (skipParentUpdate) {
        // The caller re-fetches the quote
      } else if (doUpdateParent) {
        offeringRes &&
          onOfferingChange(action!, offeringRes, undefined, false, runEvaluate);
        setIsBulkOfferingLoading(false);
      } else if (offeringRes) {
        /**
         * Bulk add suppresses the per-offering parent update and re-fetches the
         * quote once at the end. The parent still has to hear about every
         * offering it created though — inside a quote offering group container
         * this callback is what puts the new offering in the group, and skipping
         * it left bulk-added offerings ungrouped. `skipFetch` keeps the bulk
         * flow's single re-fetch, and awaiting keeps the group membership writes
         * sequential so offering N's write is in the cache before N+1 builds its
         * payload from it. The one exception is an offering whose save changed
         * quote custom fields: rules are evaluated right away so they do not
         * wait for a later edit.
         */
        await onOfferingChange(
          action!,
          offeringRes,
          undefined,
          !runEvaluate,
          runEvaluate,
        );
      }

      // If pricing formula requires getting amount back from BP for another price update, we have to perform a second submission
      if (requiresSecondUpdate) {
        onSubmit({
          data: getValues(),
          isSecondUpdate: true,
          quoteOfferingOverride: offeringRes,
          overrideOfferingDirtyFields,
        });
      }

      // Bulk add reads this to tell a created offering from a failed one
      return offeringRes;
    };

    const handleInitialQuoteItemQuantityForBulkOffering = async ({
      rateId,
      items,
    }: BulkOfferingItemQuantityHandlerProps) => {
      try {
        const isQuoteItemQuantityChangeAllowed = quoteOffering
          ? isOfferingRateChangeAllowed({
              ratesById: offeringRatesById,
              oldRateId: quoteOffering.rateId,
              newRateId: rateId!,
              isPartOfScheduledChange:
                isChildOffering || !!childQuoteOfferings.length,
            })
          : true;

        if (isQuoteItemQuantityChangeAllowed) {
          const rateRes = await fetchOfferingRate(rateId);
          if (rateRes) {
            return items.map(
              ({
                productId,
                incrementalQuantity,
              }: {
                productId: string;
                incrementalQuantity?: number | null;
              }) => {
                const quantity =
                  rateRes.productPriceRanges[productId!]?.min ?? 0; // Default quantity to 0 if not found

                return {
                  productId,
                  quantity,
                  ...(!isNil(incrementalQuantity) && {
                    incrementalQuantity: quantity,
                  }),
                };
              },
            );
          }
        }

        // return items as is if rate change is not allowed
        return items.map(
          ({
            productId,
            quantity,
            incrementalQuantity,
          }: {
            productId: string;
            quantity: number;
            incrementalQuantity?: number | null;
          }) => {
            return {
              productId,
              quantity,
              ...(!isNil(incrementalQuantity) && {
                incrementalQuantity: quantity,
              }),
            };
          },
        );
      } catch (error) {
        handleApiErrorToast(error);
        Sentry.captureException(error, {
          tags: {
            type: 'RATE_API',
          },
        });
        return [];
      }
    };

    /**
     * Sets initial quantities for quote items based on rate's min/max constraints.
     * @param rateId - The rate ID to fetch price ranges from
     * @param updatedProductItems - Items to process (defaults to watchItems if not provided)
     * @param shouldUpdateItem - Optional filter function to determine which items should be updated.
     *                           If not provided, all items will be updated.
     */
    const handleInitialQuoteItemQuantity = async (
      rateId: string,
      updatedProductItems?: IQuoteItemReqSchema[],
      shouldUpdateItem?: (item: IQuoteItemReqSchema) => boolean,
    ) => {
      try {
        const isQuoteItemQuantityChangeAllowed = quoteOffering
          ? isOfferingRateChangeAllowed({
              ratesById: offeringRatesById,
              oldRateId: quoteOffering.rateId,
              newRateId: rateId!,
              isPartOfScheduledChange:
                isChildOffering || !!childQuoteOfferings.length,
            })
          : true;

        if (!isQuoteItemQuantityChangeAllowed) {
          return;
        }

        const rateRes = await fetchOfferingRate(rateId);
        if (!rateRes) {
          return;
        }

        const itemsToProcess = updatedProductItems ?? watchItems;

        itemsToProcess.forEach(
          (
            { id, productId, quantity: currentQuantity, incrementalQuantity },
            index,
          ) => {
            // Skip if filter function is provided and returns false
            if (shouldUpdateItem && !shouldUpdateItem(itemsToProcess[index])) {
              return;
            }

            const previousQuantity = Number(
              currentQuantity ??
                quoteOffering?.items?.find(
                  ({ id: quoteItemId }) => quoteItemId === id,
                )?.quantity ??
                0,
            );

            // Calculate initial quantity from rateRes
            const min = rateRes.productPriceRanges[productId]?.min;
            const max = rateRes.productPriceRanges[productId]?.max;

            const quantity = clampValueToRange(
              previousQuantity,
              min,
              max ?? Infinity,
            );

            const inputName = `items.${index}.quantity` as const;

            setValue(inputName, quantity);

            // Set incrementalQuantity for non-prorated products
            const offeringProduct = offering?.offeringProducts?.find(
              (op) => op.product.id === productId,
            );
            const isNonProrated = isNonProratedAdvanceProduct(
              offeringProduct?.product,
            );

            if (isNonProrated) {
              const incrementalQtyInputName =
                `items.${index}.incrementalQuantity` as const;

              // For schedule changes, check if item exists in parent offering
              // If item ID is not found in parent offering, it's a newly added item
              const existsInParentOffering = parentQuoteOffering
                ? !!parentQuoteOffering.items?.find(
                    (item) => item.productId === productId,
                  )
                : false;

              // If no parent offering or item doesn't exist in parent, use quantity
              // If item exists in parent offering, use incremental change
              const incrementalQty =
                !parentQuoteOffering || !existsInParentOffering
                  ? quantity
                  : incrementalQuantity;

              setValue(incrementalQtyInputName, incrementalQty);
            }
          },
        );
      } catch (error) {
        handleApiErrorToast(error);
        Sentry.captureException(error, {
          tags: {
            type: 'RATE_API',
          },
        });
      }
    };

    const onError = (error: any) => {
      logger.warn('[FORM ERROR]', error);
      // TODO: should we notify user of error?
      // handleApiErrorToast(error); // this is not valid
    };

    const handleQuoteOffering = () => {
      if (isDirty && isValid) {
        handleSubmit((data) => onSubmit({ data }), onError)();
      }
    };

    const submitQuoteOfferingWithoutDirtyCheck = (
      overrideOfferingDirtyFields?: string[],
    ) =>
      handleSubmit(
        (data) =>
          onSubmit({
            data,
            overrideOfferingDirtyFields,
          }),
        onError,
      )();

    // Stable debounce wrapper; ref forwards to the latest closure so timers can be cancelled across renders.
    const submitWithoutDirtyCheckRef = useRef(
      submitQuoteOfferingWithoutDirtyCheck,
    );
    submitWithoutDirtyCheckRef.current = submitQuoteOfferingWithoutDirtyCheck;

    const handleQuoteOfferingWithoutDirtyCheck = useMemo(
      () =>
        debounce(
          (overrideOfferingDirtyFields?: string[]) =>
            submitWithoutDirtyCheckRef.current(overrideOfferingDirtyFields),
          300,
        ),
      [],
    );

    useEffect(
      () => () => handleQuoteOfferingWithoutDirtyCheck.cancel(),
      [handleQuoteOfferingWithoutDirtyCheck],
    );

    /**
     * Rate was saved or updated
     */
    const handleSaveCustomRate = async (rate: IRateResSchema) => {
      if (offering) {
        // add rate to offering (in memory, not api)
        await handleInitialQuoteItemQuantity(rate.id);
        if (offering.id === inactiveOffering?.id) {
          // Since the offering is inactive we should refetch the offering
          // instead of updating the offering list
          await refetchInactiveOffering();
        } else {
          addOrUpdateOfferingRate(offering.id, rate);
        }
        setValue('rateId', rate.id);
        // Ensure rate is marked as modified so that pricing is re-calculated

        await submitQuoteOfferingWithoutDirtyCheck(['rateId']);
      }
    };

    const handleEditCustomRate = async (editedRate: IRateResSchema) => {
      if (!quote?.id) {
        return;
      }
      if (offering && offering.id !== inactiveOffering?.id) {
        addOrUpdateOfferingRate(offering.id, editedRate);
      }
      await fetchQuote(quote.id);
      if (!watchRateId) {
        return;
      }
      const updatedRate = await fetchOfferingRate(watchRateId, true);
      if (!updatedRate || !offering) {
        return;
      }
      if (offering.id === inactiveOffering?.id) {
        await refetchInactiveOffering();
      } else {
        addOrUpdateOfferingRate(offering.id, updatedRate);
      }
    };

    const useQOAmendV2State = useQuoteOfferingAmendV2({
      quoteOffering,
      isChildOffering,
      childQuoteOfferings,
      revertQuoteOffering,
      onChange: onOfferingChange,
      parentQuoteOffering,
      index: offeringIndex,
      nextScheduledQuoteOffering,
      isRemoved,
      quoteOfferings,
    });
    const {
      isAmendV2Locked,
      isQOAddedToAmendment,
      handleUnlock: handleUnlockAmendV2,
      showRevertToOriginal,
      isEndingBeforeContractEnd,
      offeringEndDate,
      isOfferingEndBeforeContract,
    } = useQOAmendV2State;

    /**
     * Onetime charges are only supported on offerings the amendment itself
     * adds - on an offering carried over from the contract being amended they
     * are dropped from the payload, so the product list locks those rows
     * instead of letting the user make a selection that cannot be saved.
     * Mirrors the quantity lock in QuoteItem.
     */
    const disableOneTimeProducts =
      isAmendment && !isNewQuoteOfferingForm && !isQOAddedToAmendment;

    useEffect(() => {
      setParentScheduleChangeActiveProp &&
        setParentScheduleChangeActiveProp(scheduleChangeActive);
    }, [scheduleChangeActive, setParentScheduleChangeActiveProp]);

    useEffect(() => {
      (async () => {
        if (
          isNewQuoteOfferingForm &&
          !watchRateId &&
          rateSelects[0].length &&
          offering &&
          areAllItemsInOfferingMandatory(offering)
        ) {
          if (isAmendment && isMixedSubscriptionTiming) {
            return;
          }

          const selectedRate = getAutoSelectRate();

          setValue('rateId', selectedRate.id);
          await handleInitialQuoteItemQuantity(selectedRate.id);
          await submitQuoteOfferingWithoutDirtyCheck();
        }
      })();
    }, [isNewQuoteOfferingForm, rateSelects, watchRateId, setValue, offering]);

    // If Quote is readonly or useAmendV2 and isAmendV2Locked, then it should be readonly
    const isReadOnly =
      isReadOnlyQuote ||
      (!isNewQuoteOfferingForm && useAmendmentV2 && isAmendV2Locked);

    // Registered here, once the line-level lock state exists (and above the
    // early return below, as every hook must be): a custom-field change must
    // not auto-submit a locked Amendment V2 baseline line, which would silently
    // amend it, or a removed line.
    useHandleAnaplanCustomFieldChange(
      customerAutomationAnaplan,
      quoteOffering,
      quote?.customFields,
      isReadOnly || isRemoved,
      () => handleSubmit((data) => onSubmit({ data }), onError)(),
    );

    if (!(quote && quote.id)) {
      return null;
    }

    const quoteOfferingLoading =
      isLoading ||
      isOfferingLoading ||
      isRateLoading ||
      isOfferingDeleting ||
      scheduleChangeLoading;

    const showUnlockIcon =
      useAmendmentV2 &&
      isAmendV2Locked &&
      !isQOAddedToAmendment &&
      showRevertToOriginal;

    /** When schedule change is being created, and start date of the new segment is updated
     * We need to update the previous schedule change segment end date = new segment start date - 1 day
     * This does not update backend until schedule change is actually created.
     */
    const updatePrevScheduleChange = ({ endDate }: { endDate: string }) => {
      if (quoteOffering) {
        onOfferingChange(
          'UPDATE',
          {
            ...quoteOffering,
            endDate,
          },
          undefined,
          true,
        );
      }
    };

    const filterProductsByIds = (
      selectedItems: SelectedProductsWithinOfferingForm,
      /**
       * Flag to indicate if the id should be included in the request data.
       */
      withId: boolean,
      /**
       * Optional map for existing quantity for specific products.
       * This is used for quantity automation to set initial quantity if it is
       * dependent on other products' quantities.
       */
      productInitialQuantityMap: Record<
        string,
        { startDate: string; endDate: string; quantity: number }[]
      > = {},
    ): IQuoteItemReqSchema[] => {
      const productIdList = selectedItems.products.map(
        (product: SelectedProductsWithinOffering) =>
          product.isSelected ? product.id : null,
      );

      let currentItems =
        offering?.offeringProducts.filter(({ product }) =>
          productIdList.includes(product.id),
        ) || [];

      if (isChildOffering) {
        currentItems = currentItems.filter(({ product }) => {
          return (
            product.productType !== ProductTypeEnum.ONETIME &&
            product.productType !== ProductTypeEnum.ONETIME_PREPAID_CREDIT
          );
        });
      }

      const existingItems = getValues('items');
      const tenantDefaultDisplayUnitPriceFrequency =
        quoteSettingsData.quoteSettings?.defaultDisplayUnitPriceFrequency;

      return currentItems?.map(({ product, position }) => {
        const existingItem = existingItems.find(
          (item) => item.productId === product.id,
        );
        const defaultDisplayUnitPriceFrequency =
          product.displayUnitPriceFrequency ??
          tenantDefaultDisplayUnitPriceFrequency;

        let quantity = Number(existingItem?.quantity) || 0;
        // Apply quantity automation if needed
        if (
          !existingItem &&
          productInitialQuantityMap &&
          productInitialQuantityMap[product.id]
        ) {
          const foundItem = getQuantityAutomationForQuoteItem(
            product.id,
            quoteOffering?.startDate,
            quoteOffering?.endDate,
            productInitialQuantityMap,
          );
          if (foundItem) {
            // If product is not in existing items, but initial quantity is provided, use that
            quantity = foundItem.quantity ?? 0;
          }
        }

        const newProductData = {
          productId: product.id,
          description: product.description,
          sku: product.sku,
          customPrice: existingItem?.customPrice || undefined,
          customDiscountAmountOrPercent:
            existingItem?.customDiscountAmountOrPercent || 0,
          quantity,
          position,
          incrementalQuantity: existingItem?.incrementalQuantity || 0,
          prorationPolicy: product.prorationPolicy,
          ...(!existingItem && defaultDisplayUnitPriceFrequency
            ? {
                options: {
                  showPrices: false,
                  displayUnitPriceFrequency: defaultDisplayUnitPriceFrequency,
                },
              }
            : {}),
          ...existingItem,
        };

        if (withId) {
          return {
            ...newProductData,
            id: existingItem?.id,
          };
        }

        return newProductData;
      });
    };

    const addOfferingItems = async (
      products: SelectedProductsWithinOfferingForm,
    ) => {
      if (!offering) {
        return;
      }

      if (rateSelects[0].length) {
        if ((isAmendment && !isMixedSubscriptionTiming) || !isAmendment) {
          const selectedRate = getAutoSelectRate();
          setValue('rateId', selectedRate.id);
          await handleInitialQuoteItemQuantity(selectedRate.id);
        }
      }

      const productInitialQuantityMap =
        CUSTOMER_AUTOMATION_QuoteOffering_getInitialQuantityMap({
          customerAutomationAcuityMd,
          quote,
        });

      // Filter and set the updated product items
      const updatedProductItems = filterProductsByIds(
        products,
        false,
        productInitialQuantityMap,
      );
      setValue('items', updatedProductItems);

      // Handle quote offering and close relevant UI components
      await submitQuoteOfferingWithoutDirtyCheck();
      onManageProductModalClose();
    };

    const updateOfferingItems = async (
      products: SelectedProductsWithinOfferingForm,
    ) => {
      /**
       * The drawer turns its Save spinner on before calling this, and the
       * submit below can end without an API call (nothing dirty, invalid form),
       * so the spinner has to be turned off here rather than on the happy path
       * of onSubmit.
       */
      try {
        if (!offering) {
          return;
        }

        const rateId = watchRateId ?? offering.rates[0]?.id;

        const productInitialQuantityMap =
          CUSTOMER_AUTOMATION_QuoteOffering_getInitialQuantityMap({
            customerAutomationAcuityMd,
            quote,
          });

        const existingItems = getValues('items');
        const existingProductIds = new Set(
          existingItems.map((item) => item.productId),
        );

        // Filter and set the updated product items
        const updatedProductItems = filterProductsByIds(
          products,
          true,
          productInitialQuantityMap,
        );
        setValue('items', updatedProductItems);

        // Set initial quantities only for newly added products
        // Filter function to identify new products that weren't in the original items
        const isNewProduct = (item: IQuoteItemReqSchema) =>
          !existingProductIds.has(item.productId);

        const hasNewProducts = updatedProductItems.some(isNewProduct);

        if (hasNewProducts) {
          await handleInitialQuoteItemQuantity(
            rateId,
            updatedProductItems,
            isNewProduct,
          );
        }

        // Handle quote offering and close relevant UI components
        await submitQuoteOfferingWithoutDirtyCheck();
      } finally {
        setProductsLoading(false);
      }
    };

    const savePriceTiers = async (priceTiers: IPriceTierSettingsReq) => {
      const updatedOfferingItems = watchItems.map((item) => ({
        ...item,
        options: {
          ...item.options,
          priceDisplayMode: priceTiers.priceDisplayMode,
          displayedPriceIds: priceTiers.displayedPriceIds[item.productId],
        },
      }));
      setValue('items', updatedOfferingItems);

      handleQuoteOfferingWithoutDirtyCheck();
    };

    // If new offering, it should check offering. For quote offering (not new), should check quoteOffering
    const isCurrentOfferingOneTime =
      (!isNewQuoteOfferingForm && isOfferingOnetime) ||
      (isNewQuoteOfferingForm && offering?.type === OfferingTypesEnum.ONETIME);

    const collapsedRateSummary = getCollapsedRateSummary({
      startDate: quoteOffering?.startDate,
      endDate: quoteOffering?.endDate,
      isOneTime: isCurrentOfferingOneTime,
      itemCount: sortedWatchItems?.length ?? 0,
    });

    // Permanently locked rates render as text: a read-only select clips a long
    // rate name to its input width and scrolls it, so the name became unreadable.
    const isRateReadOnly = isReadOnly || isRemoved;

    // A description nobody can edit and that has no text has nothing to show,
    // so the header does not reserve a blank line for it.
    const isDescriptionLocked = isRemoved || !!isLocked || isReadOnly;
    const showDescriptionField =
      isOfferingOpen &&
      !isChildOffering &&
      !isLoadingOfferings &&
      (isNewQuoteOfferingForm
        ? !!offering?.description
        : !isDescriptionLocked || !!watchDescription);
    const selectedRateName = getRateName(watchRateId);

    if (
      enableBulkAddQuoteOffering &&
      !watchOffering &&
      !(isChildOffering || isReadOnlyAndNotEmptyOffering)
    ) {
      return (
        <MBox py={2}>
          <MStack pl={2}>
            {quoteOfferings && quoteOfferings?.length > 0 && (
              <MText fontSize="md" fontWeight="medium">
                Add Offerings
              </MText>
            )}
            <BulkOfferingFlow
              disabled={
                isOfferingUnselectable ||
                isOfferingDeleting ||
                isRemoved ||
                isReadOnly
              }
              items={
                getOfferingList(
                  productOfferings,
                  inactiveOffering,
                ) as IOfferingRes[]
              }
              isBulkOfferingLoading={isBulkOfferingLoading}
              quote={quote}
              quoteData={quoteData}
              displayImportOffering={displayImportOffering}
              setIsBulkOfferingLoading={setIsBulkOfferingLoading}
              onSubmit={onSubmit}
              handleInitialQuoteItemQuantityForBulkOffering={
                handleInitialQuoteItemQuantityForBulkOffering
              }
              fetchQuote={fetchQuote}
            />
          </MStack>
        </MBox>
      );
    }

    return (
      <>
        <MBox
          data-testid="offering-select-form"
          bgColor={bgColorV2}
          opacity={isRemoved ? 0.75 : 1}
        >
          {!isChildOffering && (
            <MGrid
              gridTemplateColumns={gridColumns}
              columnGap={6}
              py={2}
              alignItems="center"
              bg={!isNewQuoteOfferingForm ? 'bg.muted' : 'inherit'}
              borderRadius="md"
              px="1"
            >
              <MFlex w="full">
                {watchOffering && (
                  <OfferingToggleButton
                    isOpen={isOfferingOpen}
                    offeringIndex={offeringIndex}
                    setIsOpen={(value) => {
                      const nextIsOpen =
                        typeof value === 'function'
                          ? value(isOfferingOpen)
                          : value;
                      setIsOfferingOpen(nextIsOpen);
                      if (nextIsOpen) {
                        handleRatesOpenChange(true);
                      }
                    }}
                  />
                )}
                <MFlex w="full" alignItems="start">
                  <MFlex w="full" direction="column">
                    <MFlex w={'full'}>
                      <QuoteOfferingCollapsibleRate
                        isOpen={!isChildOffering || isOfferingOpen}
                        // The quote response already names the rate, so the
                        // title does not wait on the catalog
                        rateName={getRateName(quoteOffering?.rateId)}
                        quoteOffering={quoteOffering}
                        offeringEndDate={offeringEndDate}
                        isOfferingEndBeforeContract={
                          isOfferingEndBeforeContract &&
                          !parentScheduleChangeActive &&
                          !scheduleChangeActive
                        }
                        isRemoved={isRemoved}
                        isNewlyAddedOffering={newlyAddedOffering}
                      >
                        {!enableBulkAddQuoteOffering && (
                          <>
                            {!(
                              isChildOffering || isReadOnlyAndNotEmptyOffering
                            ) && (
                              <MFlex w="full" alignItems="center">
                                <MFormField w="full" maxW="22.125rem">
                                  <Controller
                                    control={control}
                                    name="offeringId"
                                    render={({
                                      field: { onChange, ...rest },
                                    }) => {
                                      const disabled =
                                        isOfferingUnselectable ||
                                        isOfferingDeleting ||
                                        isRateLoading ||
                                        isRemoved ||
                                        isReadOnly;

                                      return (
                                        <MCustomSelect
                                          popOverProps={{
                                            positioning: { sameWidth: false },
                                          }}
                                          isDisabled={disabled}
                                          isReadOnly={disabled}
                                          variant={'readonly'}
                                          alignSelf="center"
                                          isLazy
                                          placeholder="Select Offering"
                                          items={getOfferingList(
                                            productOfferings,
                                            inactiveOffering,
                                          )}
                                          itemTitle="name"
                                          itemValue="id"
                                          onChange={(offeringId) => {
                                            onChange(offeringId);
                                            offeringId &&
                                              handleOfferingChange(offeringId);
                                          }}
                                          flex="1"
                                          popOverContentProps={{
                                            minW: '200px',
                                          }}
                                          showQueryInput
                                          loading={isLoadingOfferings}
                                          cursor={'default'}
                                          inputProps={{
                                            css: {
                                              '&[disabled]': {
                                                cursor: 'default',
                                              },
                                            },
                                          }}
                                          {...rest}
                                        />
                                      );
                                    }}
                                  />
                                </MFormField>
                                {showDuplicatedOfferingWarn && (
                                  <MTooltip
                                    label="This offering already exists on this quote. Consider modifying the existing offering."
                                    placement="bottom-start"
                                  >
                                    <MFlex alignItems="center" ml="2">
                                      <MIcon
                                        as={MdInfo}
                                        color="tOrange.tangerine"
                                      />
                                    </MFlex>
                                  </MTooltip>
                                )}
                                {displayImportOffering && (
                                  <MBox ml={3}>
                                    <ImportQuoteOfferingsModal
                                      quote={quote}
                                      onImported={quoteData.setQuote}
                                    />
                                  </MBox>
                                )}
                              </MFlex>
                            )}
                            {!isChildOffering &&
                              isReadOnlyAndNotEmptyOffering && (
                                <MBox alignSelf="center">
                                  <MFlex mt={1} align="center" justify="center">
                                    <MText
                                      fontWeight="bold"
                                      mr={1}
                                      lineClamp={1}
                                    >
                                      {quoteOffering?.offeringName}
                                      {quoteOffering?.rateName ? ' - ' : ''}
                                      {quoteOffering?.rateName}
                                    </MText>
                                    {isLocked && (
                                      <MTooltip
                                        label="Changes cannot be made to this offering."
                                        placement="bottom-start"
                                        shouldWrapChildren
                                      >
                                        <MIcon
                                          color="tPurple.dark"
                                          as={MdLock}
                                          w={4}
                                          h={4}
                                          cursor="pointer"
                                        />
                                      </MTooltip>
                                    )}
                                  </MFlex>
                                  <MText>{quoteOffering?.description}</MText>
                                </MBox>
                              )}
                            <QuoteOfferingInactiveItem
                              offeringIndex={offeringIndex}
                              selectedOffering={offering}
                              selectedRateId={watchRateId}
                              contractStartDate={quote.contractStartDate}
                              quoteType={quote.type}
                              isNewlyAddedOffering={newlyAddedOffering}
                            />
                          </>
                        )}
                      </QuoteOfferingCollapsibleRate>
                    </MFlex>
                    {showDescriptionField && (
                      <MFormField w="full" mt={1} pr="8">
                        <Controller
                          control={control}
                          name="description"
                          render={({ field: { onChange, value } }) => {
                            const isEditDisabled =
                              isDescriptionLocked || isOfferingLoading;
                            if (isNewQuoteOfferingForm && watchOffering) {
                              return (
                                <MBox>
                                  {offering?.description && (
                                    <MText lineHeight="24px">
                                      {offering?.description}
                                    </MText>
                                  )}
                                </MBox>
                              );
                            }
                            if (watchOffering) {
                              return (
                                <MEditableTextarea
                                  value={value || ''}
                                  isDisabled={isEditDisabled}
                                  onSubmit={(currValue) => {
                                    onChange(currValue);
                                    handleQuoteOfferingWithoutDirtyCheck();
                                  }}
                                  onCancel={onChange}
                                  noOfLines={1}
                                  previewButtonProps={{ minH: '1em' }}
                                  // Secondary to the offering name above it
                                  placeholderProps={{ color: 'fg.muted' }}
                                />
                              );
                            }
                            return <div />;
                          }}
                        />
                      </MFormField>
                    )}
                  </MFlex>
                </MFlex>
              </MFlex>
              {!isOfferingOpen && (
                <QuoteOfferingDate
                  isOfferingOneTime={
                    offeringRate?.billingFrequency ===
                    RateBillingFrequencyEnum.ONETIME
                  }
                  isOpen={isOfferingOpen}
                  childOfferingCount={childQuoteOfferings?.length || 0}
                  isChildOffering={isChildOffering}
                  quoteStartDate={
                    quote.contractAmendmentDate || quote.contractStartDate
                  }
                  quoteStartDateLabel={
                    quote.contractAmendmentDate
                      ? 'amendment date'
                      : 'quote start date'
                  }
                  quoteEndDate={quote.contractEndDate}
                  startDate={quoteOffering?.startDate}
                  endDate={quoteOffering?.endDate}
                  isLastSegment={isLastSegment}
                  childQuoteOfferings={childQuoteOfferings}
                  offeringEndDate={offeringEndDate}
                  isOfferingEndBeforeContract={
                    isOfferingEndBeforeContract &&
                    !parentScheduleChangeActive &&
                    !scheduleChangeActive
                  }
                />
              )}

              {/* Subgrid of the header columns, so the expanded offering total
                  can sit in the same Amount column as the product prices */}
              <MGrid
                alignSelf="flex-start"
                gridColumn="3/-1"
                gridTemplateColumns="subgrid"
                position="relative"
              >
                {!useAmendmentV2 ||
                isQOAddedToAmendment ||
                isNewQuoteOfferingForm ? (
                  <QuoteOfferingActions
                    account={account}
                    quoteOffering={quoteOffering}
                    isChildOffering={isChildOffering}
                    childQuoteOfferings={childQuoteOfferings}
                    onChange={onOfferingChange}
                    quoteOfferingState={quoteOfferingState}
                    offering={offering}
                    quoteOfferingLoading={quoteOfferingLoading}
                    isOfferingOpen={isOfferingOpen}
                    updateQuoteOffering={updateQuoteOffering}
                    removeQuoteOffering={removeQuoteOffering}
                    quoteOfferingFormState={quoteOfferingFormState}
                    handleQuoteOfferingUpdate={({ priceDisplay }) => {
                      if (priceDisplay) {
                        setValue(
                          'options',
                          {
                            ...quoteOffering?.options,
                            priceDisplay,
                          },
                          { shouldDirty: true },
                        );
                        handleSubmit((data) => onSubmit({ data }), onError)();
                      }
                    }}
                    offeringRate={offeringRate}
                    quotePrices={quotePrices}
                    isOfferingLoading={isOfferingLoading}
                    isOfferingDeleting={isOfferingDeleting}
                    anyOfferingLoading={anyOfferingLoading}
                  />
                ) : (
                  <QuoteOfferingActionsAmendV2
                    account={account}
                    quoteOffering={quoteOffering}
                    isChildOffering={isChildOffering}
                    quoteOfferingLoading={quoteOfferingLoading}
                    isOfferingOpen={isOfferingOpen}
                    quoteOfferingState={quoteOfferingState}
                    onChange={onOfferingChange}
                    updateQuoteOffering={updateQuoteOffering}
                    quoteOfferingFormState={quoteOfferingFormState}
                    useQuoteOfferingAmendV2State={useQOAmendV2State}
                    removeQuoteOffering={removeQuoteOffering}
                    offering={offering}
                    handleAddScheduledChange={handleAddScheduledChange}
                    childQuoteOfferings={childQuoteOfferings}
                    quoteOfferings={quoteOfferings || []}
                    handleQuoteOfferingUpdate={({ priceDisplay }) => {
                      if (priceDisplay) {
                        setValue(
                          'options',
                          {
                            ...quoteOffering?.options,
                            priceDisplay,
                          },
                          { shouldDirty: true },
                        );
                        handleSubmit((data) => onSubmit({ data }), onError)();
                      }
                    }}
                    offeringRate={offeringRate}
                    monthInterval={monthInterval}
                    quotePrices={quotePrices}
                  />
                )}
              </MGrid>
            </MGrid>
          )}

          {offering &&
            watchOffering &&
            (isOfferingOpen || isRootOfferingOpen) && (
              <MBox
                onMouseOver={() => setShowUnlockTooltip(true)}
                onMouseOut={() => setShowUnlockTooltip(false)}
              >
                {/* No bottom padding: the rate grid below carries its own top padding */}
                {!isChildOffering && hasChildOfferings && (
                  <MFlex pl={7} pt={2}>
                    <ExpandCollapseButton
                      isExpanded={areAllRatesExpanded}
                      onChange={handleRatesOpenChange}
                    />
                  </MFlex>
                )}
                <MGrid
                  gridTemplateColumns={gridColumns}
                  columnGap={6}
                  py={2}
                  alignItems={isOpen ? 'flex-end' : 'center'}
                >
                  {offering && watchOffering && (
                    <MFlex gridColumn={'1 / 3'} direction="column">
                      {/* Top-aligned in both states so the chevron sits on the first line of the collapsed summary */}
                      <MFlex alignItems="start" pl={'7'}>
                        <RateToggleButton
                          isOpen={isOpen}
                          offeringIndex={offeringIndex}
                          setIsOpen={setIsOpen}
                        />
                        {offering && watchOffering && isOpen && (
                          <MFlex flexDirection="column" gap={2}>
                            <MFormField
                              label="Rate"
                              error={errors.rateId}
                              alignSelf="center"
                              // Same width in both modes keeps the date
                              // columns of every segment in line
                              w={QUOTE_RATE_FIELD_WIDTH}
                              flexShrink={0}
                            >
                              <Controller
                                control={control}
                                name="rateId"
                                render={({
                                  field: { onChange, value, ...rest },
                                }) => {
                                  const disabled =
                                    isRateLoading ||
                                    isOfferingDeleting ||
                                    isRemoved ||
                                    !watchOffering ||
                                    isReadOnly ||
                                    scheduleChangeLoading;

                                  if (isRateReadOnly) {
                                    const selectedRate = watchRateId
                                      ? offeringRatesById[watchRateId]
                                      : undefined;
                                    // Mirrors the read-only DatePicker (text
                                    // with pb 2) so the value sits on the same
                                    // line as the read-only dates beside it
                                    return (
                                      <MFlex alignItems="center" gap="2" pb="2">
                                        <MText
                                          lineClamp={1}
                                          minW={0}
                                          title={selectedRateName}
                                          {...getQuoteOfferingRateInputProps(
                                            quoteOffering,
                                            isAmendment || isRenewal,
                                            isChildOffering,
                                          )}
                                        >
                                          {selectedRateName}
                                        </MText>
                                        {selectedRate && (
                                          <QuoteOfferingRateRightEl
                                            rate={selectedRate}
                                          />
                                        )}
                                      </MFlex>
                                    );
                                  }

                                  return (
                                    <MCustomSelect
                                      isDisabled={disabled}
                                      isReadOnly={disabled}
                                      variant={
                                        disabled ? 'readonly' : 'outline'
                                      }
                                      placeholder="Select Rate"
                                      isLazy
                                      items={rateSelects[0]}
                                      disabledItems={
                                        isNewQuoteOfferingForm ||
                                        (hasNoScheduleChange &&
                                          !scheduleChangeActive)
                                          ? []
                                          : rateSelects[1].map(
                                              (rate) => rate.id,
                                            )
                                      }
                                      itemTitle="name"
                                      itemValue="id"
                                      onChange={async (e) => {
                                        onChange(e);
                                        await handleInitialQuoteItemQuantity(
                                          `${e}`,
                                        );
                                        await submitQuoteOfferingWithoutDirtyCheck();
                                      }}
                                      flex={isChildOffering ? 'unset' : '1'}
                                      renderRightElement={({
                                        value: rateValue,
                                      }) =>
                                        getRateRightElement({
                                          value: rateValue,
                                          offeringRatesById,
                                        })
                                      }
                                      inputProps={getQuoteOfferingRateInputProps(
                                        quoteOffering,
                                        isAmendment || isRenewal,
                                        isChildOffering,
                                      )}
                                      value={value}
                                      renderItemContent={RateItemContent}
                                      showQueryInput
                                      {...rest}
                                    />
                                  );
                                }}
                              />
                            </MFormField>
                          </MFlex>
                        )}
                        <MBox alignSelf="end">
                          <QuoteOfferingInactiveRateItem
                            offeringIndex={offeringIndex}
                            selectedOffering={offering}
                            selectedRateId={watchRateId}
                            contractStartDate={quote.contractStartDate}
                            rates={rateSelects[0]}
                            quoteType={quote.type}
                            isNewlyAddedOffering={newlyAddedOffering}
                          />
                        </MBox>
                        {isOpen && (
                          <MFormField
                            label={
                              isCurrentOfferingOneTime ? 'Date' : 'Start Date'
                            }
                            alignSelf="center"
                            maxW={QUOTE_DATE_FIELD_WIDTH}
                            ml="4"
                          >
                            <QuoteOfferingScheduledEffectiveDate
                              isChildOffering={isChildOffering}
                              isDisabled={
                                (!quoteOfferingDateAndScheduledChangeRate &&
                                  isChildOffering &&
                                  // Amend and !AmendV2 or AmendV2 and isAmendV2Locked, then lock
                                  // We need to unlock useAmendV2 and !isAmendV2Locked
                                  ((isAmendment && !useAmendmentV2) ||
                                    (useAmendmentV2 && isAmendV2Locked))) ||
                                isReadOnly ||
                                isRemoved ||
                                (isNewQuoteOfferingForm &&
                                  !allowChangingQuoteOfferingStartAndEndDate &&
                                  !useAmendmentV2) ||
                                isOfferingDeleting ||
                                (!isChildOffering &&
                                  !allowChangingQuoteOfferingStartAndEndDate &&
                                  !useAmendmentV2) ||
                                scheduleChangeLoading
                              }
                              isReadOnly={
                                isReadOnly ||
                                (!isChildOffering &&
                                  !useAmendmentV2 &&
                                  !allowChangingQuoteOfferingStartAndEndDate) ||
                                isRemoved
                              }
                              isOfferingLoading={isOfferingLoading}
                              quote={quote}
                              control={control}
                              priorScheduledQuoteOffering={
                                priorScheduledQuoteOffering
                              }
                              nextScheduledQuoteOffering={
                                nextScheduledQuoteOffering
                              }
                              onChange={(date) => {
                                if (isChildOffering) {
                                  setValue(
                                    'schedule.parentQuoteOfferingId',
                                    quoteOffering?.parentQuoteOfferingId!,
                                  );
                                }
                                handleQuoteOfferingWithoutDirtyCheck();
                              }}
                              parentQuoteOffering={quoteOffering}
                              offering={offering}
                              monthInterval={monthInterval}
                              isUsingDelayedBilling={isUsingDelayedBilling}
                              isAmendment={isAmendment}
                            />
                          </MFormField>
                        )}
                        {/* Do not show end date when it's one time offering https://monetizenow.atlassian.net/browse/BP-11186 */}
                        {isOpen && !isCurrentOfferingOneTime && (
                          <MFormField
                            label="End Date"
                            alignSelf="center"
                            maxW={QUOTE_DATE_FIELD_WIDTH}
                            ml="4"
                            tooltip={
                              isEndingBeforeContractEnd &&
                              !scheduleChangeActive &&
                              !isOfferingOnetime
                                ? 'Offering ends before end of Contract'
                                : ''
                            }
                            tooltipColor="tOrange.tangerine"
                            onMouseOver={(ev) => {
                              ev.stopPropagation();
                            }}
                          >
                            <QuoteOfferingScheduledEndDate
                              isChildOffering={isChildOffering}
                              isDisabled={
                                (!quoteOfferingDateAndScheduledChangeRate &&
                                  isChildOffering &&
                                  // Amend and !AmendV2 or AmendV2 and isAmendV2Locked, then lock
                                  // We need to unlock useAmendV2 and !isAmendV2Locked
                                  ((isAmendment && !useAmendmentV2) ||
                                    (useAmendmentV2 && isAmendV2Locked))) ||
                                isReadOnly ||
                                isRemoved ||
                                (isNewQuoteOfferingForm &&
                                  !allowChangingQuoteOfferingStartAndEndDate &&
                                  !useAmendmentV2) ||
                                isOfferingDeleting ||
                                (!isChildOffering &&
                                  !allowChangingQuoteOfferingStartAndEndDate &&
                                  !useAmendmentV2) ||
                                scheduleChangeLoading
                              }
                              isReadOnly={
                                isReadOnly ||
                                (!useAmendmentV2 &&
                                  !allowChangingQuoteOfferingStartAndEndDate) ||
                                isRemoved
                              }
                              isOfferingLoading={isOfferingLoading}
                              quote={quote}
                              control={control}
                              priorScheduledQuoteOffering={
                                priorScheduledQuoteOffering
                              }
                              nextScheduledQuoteOffering={
                                nextScheduledQuoteOffering
                              }
                              onChange={() => {
                                if (isChildOffering) {
                                  setValue(
                                    'schedule.parentQuoteOfferingId',
                                    quoteOffering?.parentQuoteOfferingId!,
                                  );
                                }
                                handleQuoteOfferingWithoutDirtyCheck();
                              }}
                              parentQuoteOffering={quoteOffering}
                              offering={offering}
                              monthInterval={monthInterval}
                              isUsingDelayedBilling={isUsingDelayedBilling}
                            />
                          </MFormField>
                        )}
                        {!isOpen &&
                          offering && (
                            // Rate name sits in the expanded row's Rate column
                            // and the date range starts where its Start Date
                            // field does, so both stay put when the segment is
                            // collapsed or expanded. The product count sits
                            // under the rate name and the duration is centered
                            // under the range. 24px first lines match the
                            // toggle button beside them.
                            <MFlex alignItems="start">
                              <MBox w={QUOTE_RATE_FIELD_WIDTH} flexShrink={0}>
                                <MFlex gap="2" alignItems="center" h="6">
                                  <MText lineClamp={1} minW={0}>
                                    {selectedRateName}
                                  </MText>
                                  {watchRateId &&
                                    offeringRatesById[watchRateId] && (
                                      <QuoteOfferingRateRightEl
                                        rate={offeringRatesById[watchRateId]}
                                      />
                                    )}
                                </MFlex>
                                <MText
                                  fontSize="xs"
                                  color="fg.muted"
                                  lineClamp={1}
                                >
                                  {collapsedRateSummary.productCountLabel}
                                </MText>
                              </MBox>
                              <MFlex
                                direction="column"
                                alignItems="center"
                                flexShrink={0}
                                ml="4"
                              >
                                <MText
                                  lineClamp={1}
                                  fontWeight="500"
                                  lineHeight="1.5rem"
                                >
                                  {collapsedRateSummary.dateLabel}
                                </MText>
                                {collapsedRateSummary.durationLabel && (
                                  <MText
                                    fontSize="xs"
                                    color="fg.muted"
                                    lineClamp={1}
                                  >
                                    {collapsedRateSummary.durationLabel}
                                  </MText>
                                )}
                              </MFlex>
                            </MFlex>
                          )}
                      </MFlex>
                    </MFlex>
                  )}

                  <MBox />

                  {isQuoteOfferingDiscountAvailable ? (
                    <QuoteOfferingDiscount
                      isOpen={isOpen}
                      currency={quote.currency}
                      quoteOffering={quoteOffering}
                      errors={errors}
                      control={control}
                      offeringId={watchOffering}
                      rateId={watchRateId}
                      discounts={mergedDiscounts || null}
                      discountIds={watchDiscountIds}
                      disabled={
                        (isAmendment && !useAmendmentV2 && hasChildOfferings) ||
                        isRateLoading ||
                        isOfferingDeleting ||
                        scheduleChangeLoading
                      }
                      handleQuoteOfferingWithoutDirtyCheck={
                        handleQuoteOfferingWithoutDirtyCheck
                      }
                      isRemoved={isRemoved}
                      isReadOnly={isLocked || isReadOnly}
                      setValue={setValue}
                      discountDisabledProductIds={discountDisabledProductIds}
                      discountApplicationMethod={
                        quote.discountApplicationMethod
                      }
                    />
                  ) : (
                    <MBox /> // we still need this because we use grid system and need placeholder.
                  )}

                  <MFlex
                    alignItems="center"
                    justifyContent="end"
                    gridColumn="span 3"
                    alignSelf="end"
                  >
                    {quoteOffering?.id &&
                      offering &&
                      watchRateId &&
                      !isNewQuoteOfferingForm && (
                        <MFlex alignItems="center" justifyContent="end">
                          <QuoteDrawer
                            offering={offering}
                            allowPriceCustomization={allowPriceCustomization}
                            account={account}
                            isOfferingOpen={isOpen}
                            quoteOffering={quoteOffering}
                            offeringRate={offeringRate}
                            orderedPricesUnderProduct={
                              orderedPricesUnderProduct
                            }
                            isDisabledButton={
                              isOfferingLoading || isOfferingDeleting
                            }
                            disabled={
                              isOfferingLoading ||
                              isOfferingDeleting ||
                              isRemoved ||
                              isAmendV2Locked
                            }
                            onRateSaved={handleSaveCustomRate}
                            onRateEdited={handleEditCustomRate}
                            setOfferingRate={setOfferingRate}
                            setOrderedPricesUnderProduct={
                              setOrderedPricesUnderProduct
                            }
                            onDrawerToggle={setDrawerOpen}
                            offeringType={offering.type}
                            modalConfig={{
                              isOpen: isDrawerOpen,
                              onClose: onDrawerClose,
                              onOpen: onDrawerOpen,
                            }}
                            updateOfferingItems={updateOfferingItems}
                            isQuoteOfferingProcessing={
                              isQuoteOfferingProcessing
                            }
                            areProductsOptional={
                              !areAllItemsInOfferingMandatory(offering)
                            }
                            disableOneTimeProducts={disableOneTimeProducts}
                            setProductsLoading={setProductsLoading}
                            isProductsLoading={isProductsLoading}
                            savePriceTiers={savePriceTiers}
                          />
                        </MFlex>
                      )}
                    {!isOpen && (
                      <QuoteOfferingAmount
                        quoteOffering={quoteOffering}
                        currency={quote?.currency}
                        isRateSegment
                      />
                    )}
                    <MBox alignSelf="center" justifySelf="end">
                      {(isOfferingLoading || scheduleChangeLoading) && (
                        <MFlex
                          justifyContent="center"
                          alignItems="center"
                          minW="4"
                        >
                          <MCircularProgress isIndeterminate size="2xs" />
                        </MFlex>
                      )}
                      {isChildOffering &&
                      // if not amend, or amendV2 and newly added offerign, or amendV2 and unlocked
                      // then we show X button to delete schedule change
                      (!isAmendment ||
                        (isAmendment && isQOAddedToAmendment) ||
                        (useAmendmentV2 && !isAmendV2Locked)) &&
                      !isOfferingLoading &&
                      !isReadOnly &&
                      !scheduleChangeLoading &&
                      !isOfferingDeleting &&
                      !isRemoved ? (
                        <MCloseButton
                          minH="6"
                          minW="6"
                          mr="1"
                          onClick={() => handleRemoveScheduledChange()}
                        />
                      ) : (
                        <MBox minW="6" />
                      )}

                      {showUnlockIcon &&
                        !isOfferingLoading &&
                        !scheduleChangeLoading &&
                        !isOfferingDeleting &&
                        !isReadOnly && (
                          <MTooltip
                            label="Click to make this segment editable"
                            placement="bottom-start"
                            isOpen={showUnlockTooltip}
                            onOpenChange={({ open }) => {
                              if (open) {
                                setShowUnlockTooltip(true);
                              }
                            }}
                          >
                            <MCustomIconButton
                              variant="icon"
                              btnSize={4}
                              containerSize={6}
                              p={3}
                              icon={MdLock}
                              aria-label="Unlock segment"
                              onClick={() => handleUnlockAmendV2()}
                            />
                          </MTooltip>
                        )}
                    </MBox>
                  </MFlex>

                  {isOpen && !areAllItemsInOfferingMandatory(offering) && (
                    <MFlex ml={8} pl={1} mt={2}>
                      <ManageProducts
                        // Same lock as the quote items it adds and removes.
                        isManageProductDisabled={
                          isReadOnly ||
                          !!isLocked ||
                          isAmendV2Locked ||
                          isRemoved
                        }
                        quoteOffering={quoteOffering}
                        watchRateId={watchRateId}
                        offering={offering}
                        quoteItems={sortedWatchItems}
                        updateOfferingItems={addOfferingItems}
                        disableOneTimeProducts={disableOneTimeProducts}
                        modalConfig={{
                          isOpen: isManageProductModalOpen,
                          onClose: handleOnModalClose,
                          onDrawerClose,
                          onDrawerOpen,
                        }}
                        setDrawerOpen={setDrawerOpen}
                      />
                    </MFlex>
                  )}
                </MGrid>

                <MDivider />

                <QuoteItems
                  offering={offering}
                  parentQuoteOffering={parentQuoteOffering}
                  offeringRate={offeringRate}
                  quoteOffering={quoteOffering}
                  priorScheduledQuoteOffering={priorScheduledQuoteOffering}
                  rateId={watchRateId}
                  quoteItems={sortedWatchItems}
                  watchItems={watchItems ?? undefined}
                  control={control}
                  isOpen={isOpen}
                  handleQuoteOffering={handleQuoteOffering}
                  handleQuoteOfferingWithoutDirtyCheck={
                    handleQuoteOfferingWithoutDirtyCheck
                  }
                  isRemoved={isRemoved}
                  isReadOnly={isLocked || isReadOnly || isAmendV2Locked}
                  setValue={setValue}
                  setError={setError}
                  quotePrices={quotePrices}
                  displayConfig={displayConfig}
                  isQOAddedToAmendment={isQOAddedToAmendment}
                  isDisabled={scheduleChangeLoading}
                  orderedPricesUnderProduct={orderedPricesUnderProduct}
                />
              </MBox>
            )}
        </MBox>
        <QuoteOfferingScheduleChange
          quoteOffering={quoteOffering}
          isChildOffering={isChildOffering}
          childQuoteOfferings={childQuoteOfferings}
          parentQuoteOffering={parentQuoteOffering}
          nextScheduledQuoteOffering={nextScheduledQuoteOffering}
          priorScheduledQuoteOffering={priorScheduledQuoteOffering}
          quoteOfferingState={quoteOfferingState}
          offering={offering}
          isOfferingOpen={isOfferingOpen}
          scheduleChangeActive={scheduleChangeActive}
          isOpen={isOpen}
          isLoadingOfferings={isLoadingOfferings}
          offeringRatesById={offeringRatesById}
          handleScheduleChangeInitiated={handleScheduleChangeInitiated}
          handleAddScheduledChange={handleAddScheduledChange}
          scheduleChangeLoading={scheduleChangeLoading}
          setScheduleChangeActive={setScheduleChangeActive}
          inactiveRate={inactiveRate}
          rateSelects={rateSelects}
          scheduleChangeState={scheduleChangeState}
          isReadOnly={isReadOnly}
          updatePrevScheduleChange={updatePrevScheduleChange}
          onOfferingChange={onOfferingChange}
        />
        {/* Child offerings (scheduled changes) */}
        {!isChildOffering &&
          isOfferingOpen &&
          Array.isArray(childQuoteOfferings) &&
          childQuoteOfferings.map((childQuoteOffering, i) => (
            <QuoteOffering
              key={`${childQuoteOffering.id}-${childQuoteOffering.rateId}`}
              index={offeringIndex + i + 1}
              isChildOffering
              quoteOffering={childQuoteOffering}
              priorScheduledQuoteOffering={
                i === 0 ? quoteOffering : childQuoteOfferings[i - 1]
              }
              nextScheduledQuoteOffering={childQuoteOfferings[i + 1]}
              childQuoteOfferings={childQuoteOfferings}
              quotePrices={quotePrices}
              isRootOfferingOpen={isOfferingOpen}
              parentQuoteOffering={quoteOffering}
              onChange={onOfferingChange}
              setParentScheduleChangeActiveProp={setParentScheduleChangeActive}
              quoteOfferings={quoteOfferings}
              isRateOpen={childRateOpenStates[childQuoteOffering.id] ?? true}
              onIsRateOpenChange={(open) =>
                setChildRateOpenStates((prev) => ({
                  ...prev,
                  [childQuoteOffering.id]: open,
                }))
              }
            />
          ))}
      </>
    );
  },
);
export default QuoteOffering;
